import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Check as CheckIcon } from "@mui/icons-material";
import { Close as CloseIcon } from "@mui/icons-material";
import { KeyboardArrowDown as KeyboardArrowDown } from "@mui/icons-material";
import { Box, Button, ButtonBase, FilledInput, Popover, Stack, TextField, } from "@mui/material";
import throttle from "lodash.throttle";
import { bindPopover, bindTrigger, usePopupState, } from "material-ui-popup-state/es/hooks.js";
import React from "react";
import { HexColorPicker } from "react-colorful";
import { useLatest } from "react-use";
import { PopoverOpenButton, } from "../popover/PopoverOpenButton.js";
import { withStyles } from "../styles.js";
const isValid = (color) => color.match(/^#[a-f\d]{6}$/i);
const transparent = {
    backgroundImage: `linear-gradient(45deg, #999 25%, transparent 25%),
      linear-gradient(45deg, transparent 75%, #999 75%),
      linear-gradient(45deg, transparent 75%, #999 75%),
      linear-gradient(45deg, #999 25%, #fff 25%)`,
    backgroundSize: "6px 6px",
    backgroundPosition: "0 0, 0 0, -3px -3px, 3px 3px",
};
const ColorfulPopover = withStyles(Popover, {
    paper: {
        "& .react-colorful__saturation": {
            borderRadius: "0 !important",
        },
        "& .react-colorful__last-control": {
            borderRadius: "0 !important",
        },
    },
});
const ColorButtonBase = withStyles(({ $color, ...props }) => (_jsx(ButtonBase, { ...props })), (theme, props) => ({
    root: {
        height: 18,
        width: 18,
        position: "relative",
        top: 8,
        marginRight: 8,
        flexGrow: 0,
        flexShrink: 0,
        borderRadius: `${theme.shape.borderRadius}px`,
        backgroundColor: isValid(props.$color) ? props.$color : "transparent",
        ...(!isValid(props.$color) ? transparent : undefined),
    },
}));
export const ColorPicker = ({ id, name, error, required, disabled, placeholder, helperText, label, value, onChange, fullWidth = true, }) => {
    const popupState = usePopupState({
        variant: "popover",
        popupId: "colorPickerPopover",
    });
    const onChangeLatest = useLatest(onChange);
    // We need to slightly throttle change callback from react-colorful, since it updates values too frequently
    const handleChange = React.useCallback(throttle((value) => {
        onChangeLatest.current(value);
    }, 100), [onChangeLatest]);
    return (_jsxs(_Fragment, { children: [_jsx(TextField, { id: id, name: name, error: error, helperText: helperText, required: required, disabled: disabled, fullWidth: fullWidth, label: label, placeholder: placeholder, value: value, onChange: event => {
                    onChange(event.target.value);
                }, slotProps: {
                    input: {
                        startAdornment: (_jsx(ColorButtonBase, { disabled: disabled, "$color": value, ...bindTrigger(popupState) })),
                    },
                } }), _jsx(ColorfulPopover, { ...bindPopover(popupState), anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                }, transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                }, children: _jsx(HexColorPicker, { color: value, onChange: handleChange }) })] }));
};
export const ColorButtonPicker = ({ value, onChange, buttonProps, }) => {
    const [open, setOpen] = React.useState(false);
    const popoverOpenButtonRef = React.useRef();
    const [internalValue, setInternalValue] = React.useState(value);
    const stateRef = useLatest({
        internalValue,
        value,
        onChange,
        setInternalValue,
    });
    // react on outside change
    React.useEffect(() => {
        if (stateRef.current.internalValue !== value) {
            setInternalValue(value);
        }
    }, [stateRef, value]);
    // We need to slightly throttle change callback from react-colorful, since it updates values too frequently
    const handleChange = React.useCallback(throttle((value) => {
        stateRef.current.setInternalValue(value);
    }, 100), [stateRef]);
    return (_jsx(PopoverOpenButton, { onOpen: () => setOpen(true), onClose: () => setOpen(false), popoverRef: popoverOpenButtonRef, anchorOrigin: {
            vertical: "bottom",
            horizontal: "left",
        }, transformOrigin: {
            vertical: "top",
            horizontal: "left",
        }, content: _jsxs(Stack, { spacing: 1, p: 1, sx: {
                "& .react-colorful__saturation": {
                    borderRadius: "0 !important",
                },
                "& .react-colorful__last-control": {
                    borderRadius: "0 !important",
                },
            }, children: [_jsx(FilledInput, { size: "extra-small", placeholder: "HEX", value: internalValue, onChange: e => {
                        handleChange(e.target.value || "");
                    }, startAdornment: _jsx(Box, { display: "flex", width: "18px", height: "18px", sx: theme => ({
                            flexGrow: 0,
                            flexShrink: 0,
                            borderRadius: `${theme.shape.borderRadius}px`,
                            backgroundColor: isValid(internalValue)
                                ? internalValue
                                : "transparent",
                            ...(!isValid(internalValue) ? transparent : undefined),
                        }) }) }), _jsx(HexColorPicker, { color: internalValue, onChange: handleChange }), _jsxs(Stack, { direction: "row", justifyContent: "flex-end", spacing: 1, children: [_jsx(Button, { size: "extra-small", color: "primary", variant: "text", onClick: () => popoverOpenButtonRef.current?.handleClose(), children: _jsx(CloseIcon, {}) }), _jsx(Button, { size: "extra-small", color: "primary", variant: "contained", disabled: Boolean(!internalValue || !isValid(internalValue)), onClick: () => {
                                onChange(internalValue);
                                popoverOpenButtonRef.current?.handleClose();
                            }, children: _jsx(CheckIcon, {}) })] })] }), children: _jsx(Button, { ...buttonProps, endIcon: _jsx(KeyboardArrowDown, { sx: {
                    transition: "transform 0.2s ease-out",
                    ...(open ? { transform: `rotate(-180deg)` } : undefined),
                } }), children: _jsx(Box, { display: "flex", width: "18px", height: "18px", sx: theme => ({
                    flexGrow: 0,
                    flexShrink: 0,
                    borderRadius: `${theme.shape.borderRadius}px`,
                    backgroundColor: isValid(value) ? value : "transparent",
                    ...(!isValid(value) ? transparent : undefined),
                }) }) }) }));
};
//# sourceMappingURL=ColorPicker.js.map